import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable } from "bindable-data/dist/binding_data/Bindable";
import { NotifyProperty } from "bindable-data/dist/binding_data/NotifyProperty";
import { nameof } from "bindable-data";
import { BundleResourcesPackage, IPackageRegistrator } from "resource-system";
import { InjectableInterfaces } from "../../../composition/InjectableInterfaces";
import { GameUIResourcesPackage } from "../resources/GameUIResourcesPackage";
import { InfoResourcesPackage } from "../../info/resources/InfoResourcesPackage";
import { CommonUIResourcesPackage } from "../../common/resources/CommonUIResourcesPackage";
import { SettingsResourcesPackage } from "../../settings/resources/SettingsResourcesPackage";
import { LoseSceneResourcesPackage } from "../../lose/resources/LoseSceneResourcesPackage";
import { IGameDistributionSdk } from "../../../gamedistribution/interfaces/IGameDistributionSdk";

@injectable()
export class PreloaderModel extends Bindable {
    public readonly loadingProgress: NotifyProperty<number>;
    private readonly packagesToPreload: Map<new () => BundleResourcesPackage, BundleResourcesPackage>;
    private readonly packagesAmount: number;
    private progress: number;

    public constructor(
        @inject(InjectableInterfaces.IPackageRegistrator) private readonly packageRegistrator: IPackageRegistrator,
        @inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk
    ) {
        super();
        this.loadingProgress = new NotifyProperty(this, nameof(this, "loadingProgress"), 0);
        this.packagesToPreload = new Map<new () => BundleResourcesPackage, BundleResourcesPackage>([
            [GameUIResourcesPackage, new GameUIResourcesPackage()],
            [CommonUIResourcesPackage, new CommonUIResourcesPackage()],
            [InfoResourcesPackage, new InfoResourcesPackage()],
            [SettingsResourcesPackage, new SettingsResourcesPackage()],
            [LoseSceneResourcesPackage, new LoseSceneResourcesPackage()]
        ]);
        this.packagesAmount = this.packagesToPreload.size;
        this.progress = 0;
    }

    public async startPreloadingPackages(): Promise<void> {
        const loadingTasks = new Array<Promise<BundleResourcesPackage>>();
        for (const pkg of this.packagesToPreload.values()) {
            let previousProgress = 0;
            pkg.onLoadingProgress.add((packageProgress: number) => {
                this.progress -= previousProgress / this.packagesAmount;
                this.progress += packageProgress / this.packagesAmount;
                previousProgress = packageProgress;
                this.loadingProgress.setValue(this.progress);
            }, this);
            try {
                loadingTasks.push(pkg.load());
            } catch (e) {
                console.log(e);
            }
        }
        await Promise.all([...loadingTasks, this.gdSdk.initialize()]);
        for (const pkgType of this.packagesToPreload.keys()) {
            const pkg = this.packagesToPreload.get(pkgType);
            this.packageRegistrator.registerPackage(pkgType, pkg);
            pkg.onLoadingProgress.removeAll();
        }
    }

}
